import type { LocalDashboardPayload } from '../local_dashboard/types.js';

export interface DashboardDataProvider {
  load(identityToken: string): Promise<LocalDashboardPayload>;
  refreshFromNinja(identityToken: string): Promise<LocalDashboardPayload>;
}

export class LocalDashboardDataProvider implements DashboardDataProvider {
  async load(identityToken: string): Promise<LocalDashboardPayload> {
    return await requestDashboard('/api/dashboard', 'GET', identityToken);
  }

  async refreshFromNinja(identityToken: string): Promise<LocalDashboardPayload> {
    return await requestDashboard('/api/ninja-backup/refresh', 'POST', identityToken);
  }
}

async function requestDashboard(
  path: string,
  method: 'GET' | 'POST',
  identityToken: string,
): Promise<LocalDashboardPayload> {
  if (identityToken.length === 0) throw new Error('Authenticated session is unavailable');
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: { Authorization: `Bearer ${identityToken}` },
      cache: 'no-store',
    });
  } catch {
    throw new Error('Local dashboard service is unavailable');
  }
  if (!response.ok) throw new Error('Local dashboard request was not authorized or available');
  const payload = await response.json() as unknown;
  if (!isDashboardPayload(payload)) throw new Error('Local dashboard returned an invalid response');
  return payload;
}

function isDashboardPayload(value: unknown): value is LocalDashboardPayload {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.dashboard === 'object' && record.dashboard !== null
    && Array.isArray(record.inventory)
    && typeof record.inventorySummary === 'object' && record.inventorySummary !== null
  );
}
